export const PASSWORD_UPDATE_TIMEOUT_MESSAGE =
  'Saving your new password is taking longer than expected. Check your connection and try again.';

// Finish before the page's 15-second deadline so Supabase releases its auth
// lock. A UI Promise.race alone leaves a retry queued behind the old request.
const PASSWORD_UPDATE_REQUEST_TIMEOUT_MS = 12000;

function isPasswordUpdate(input: RequestInfo | URL, init?: RequestInit): boolean {
  if (init?.method?.toUpperCase() !== 'PUT' || typeof init.body !== 'string') return false;
  try {
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
    return url.pathname === '/auth/v1/user' && typeof JSON.parse(init.body).password === 'string';
  } catch {
    return false;
  }
}

export function withPasswordUpdateTimeout(fetcher: typeof fetch): typeof fetch {
  return async (input, init) => {
    if (!isPasswordUpdate(input, init)) return fetcher(input, init);

    const controller = new AbortController();
    const signal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
    const forwardAbort = () => controller.abort(signal?.reason);
    if (signal?.aborted) forwardAbort();
    else signal?.addEventListener('abort', forwardAbort, { once: true });
    let timer: ReturnType<typeof setTimeout>;
    const deadline = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        const error = new Error(PASSWORD_UPDATE_TIMEOUT_MESSAGE);
        controller.abort(error);
        reject(error);
      }, PASSWORD_UPDATE_REQUEST_TIMEOUT_MS);
    });

    const request = async () => {
      const response = await fetcher(input, { ...init, signal: controller.signal });
      // fetch resolves at headers. Supabase still needs the JSON body before
      // releasing its lock, so buffer this small auth response under the same
      // deadline and return an unread response to the SDK.
      const body = response.body ? await response.arrayBuffer() : null;
      return new Response(body, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    };

    try {
      return await Promise.race([request(), deadline]);
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', forwardAbort);
    }
  };
}
